import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import { CREATE_COMPANY } from "../../graph/companies/mutations";
import formOptions from "../../config/formOptions.json";

const client = new GraphQLClient(graphqlEndpoint);

const companySchema = yup.object().shape({
  legalName: yup.string().trim().required("Legal name is required"),
  tradingName: yup.string().nullable(),
  industry: yup.string().nullable(),
  companySizeBand: yup.string().nullable(),
  websiteURL: yup.string().url("Enter a valid URL").nullable(),
  linkedInURL: yup.string().url("Enter a valid URL").nullable(),
  phone: yup.string().nullable(),
  email: yup.string().email("Enter a valid email").nullable(),
  status: yup.string().required("Status is required"),
});

const initialCompanyValues = {
  legalName: "",
  tradingName: "",
  industry: "",
  companySizeBand: "",
  websiteURL: "",
  linkedInURL: "",
  phone: "",
  email: "",
  status: "PROSPECT",
};


const CreateCompanyForm = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const cleanedValues = { ...values };

      Object.keys(cleanedValues).forEach((key) => {
        if (cleanedValues[key] === "") cleanedValues[key] = null;
      });

      await client.request(CREATE_COMPANY, { input: cleanedValues });

      setSnackbar({
        open: true,
        message: "Company created successfully",
        severity: "success",
      });

      resetForm();
      navigate("/manageCompanies");
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("Error creating company:", err);

      setSnackbar({
        open: true,
        message: `Failed to create company: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Header
        title="CREATE COMPANY"
        subtitle="Add a company to your market database"
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialCompanyValues}
        validationSchema={companySchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Legal Name"
                name="legalName"
                value={values.legalName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.legalName && !!errors.legalName}
                helperText={touched.legalName && errors.legalName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Trading Name"
                name="tradingName"
                value={values.tradingName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.tradingName && !!errors.tradingName}
                helperText={touched.tradingName && errors.tradingName}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Industry</InputLabel>
                <Select
                  name="industry"
                  value={values.industry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Industry"
                >
                  <MenuItem value="">None</MenuItem>
                  {formOptions.industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Company Size</InputLabel>
                <Select
                  name="companySizeBand"
                  value={values.companySizeBand}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Company Size"
                >
                  <MenuItem value="">None</MenuItem>
                  {formOptions.companySizeBandOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Status"
                >
                  {
                  formOptions.companyStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Website URL"
                name="websiteURL"
                value={values.websiteURL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.websiteURL && !!errors.websiteURL}
                helperText={touched.websiteURL && errors.websiteURL}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="LinkedIn URL"
                name="linkedInURL"
                value={values.linkedInURL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.linkedInURL && !!errors.linkedInURL}
                helperText={touched.linkedInURL && errors.linkedInURL}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Phone"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <ActionButton type="submit" tone="success">
                Create Company
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateCompanyForm;